import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth-context";
import { extractErrorMessage } from "@/lib/api";
import logoIcon from "@/assets/brand/logo-icon.svg";

export const Route = createFileRoute("/register")({
  component: RegisterPage,
});

function RegisterPage() {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [hostIntent, setHostIntent] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password !== passwordConfirm) {
      toast.error("Passwords don't match.");
      return;
    }
    setSubmitting(true);
    try {
      await register({
        name,
        email,
        password,
        password_confirmation: passwordConfirm,
      });
      /*
        The checkbox is an intent, not a role. It used to register the account
        as a host outright, which skipped verification entirely; now it just
        decides where someone lands, and the application decides the rest.
      */
      toast.success(hostIntent ? "Welcome! Let's get you verified." : "Welcome to CavaYo!");
      navigate({ to: hostIntent ? "/become-host" : "/" });
    } catch (err) {
      toast.error(extractErrorMessage(err, "Registration failed"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-primary/10 via-background to-accent/10 px-4 py-12">
      <div className="w-full max-w-md rounded-3xl border border-border bg-card p-8 shadow-[var(--shadow-elevated)]">
        <Link to="/" className="mb-6 flex flex-col items-center gap-2">
          <img src={logoIcon} alt="CavaYo" className="h-12 w-12 rounded-xl object-contain" />
          {/*
            One heading, not an <h3> nested inside an <h2>. The "@" is a
            typographic flourish in the middle of a phrase — a span, not a
            second-level section of the first heading.
          */}
          <h1 className="font-display text-lg font-semibold text-primary">
            Feel <span className="align-middle text-ink">@</span> Home
          </h1>
        </Link>
        <form onSubmit={onSubmit} className="mt-6 space-y-4">
          <Field label="Full name">
            <input
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="auth-input"
              autoComplete="name"
              autoCapitalize="words"
              enterKeyHint="next"
            />
          </Field>
          <Field label="Email">
            <input
              required
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="auth-input"
              autoComplete="email"
              inputMode="email"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              enterKeyHint="next"
            />
          </Field>
          <Field label="Password">
            <input
              required
              type="password"
              minLength={8}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="auth-input"
              autoComplete="new-password"
            />
          </Field>
          <Field label="Confirm password">
            <input
              required
              type="password"
              minLength={8}
              value={passwordConfirm}
              onChange={(e) => setPasswordConfirm(e.target.value)}
              className="auth-input"
              autoComplete="new-password"
            />
          </Field>

          <label className="flex cursor-pointer items-start gap-3 rounded-xl border border-border bg-muted/30 p-3 transition hover:border-primary/40">
            <input
              type="checkbox"
              checked={hostIntent}
              onChange={(e) => setHostIntent(e.target.checked)}
              className="mt-0.5 h-4 w-4 accent-primary"
            />
            <span className="flex-1">
              <span className="block text-sm font-semibold text-ink">I want to host on CavaYo</span>
              <span className="block text-xs text-ink-muted">
                We'll take you straight to the short verification form. It needs one photo ID and a
                few minutes.
              </span>
            </span>
          </label>

          <button
            type="submit"
            disabled={submitting}
            className="h-12 w-full rounded-xl bg-primary text-sm font-bold text-primary-foreground transition hover:opacity-90 disabled:opacity-60"
          >
            {submitting ? "Creating account…" : "Create account"}
          </button>
        </form>

        <p className="mt-6 text-center text-sm text-ink-muted">
          Already a member?{" "}
          <Link to="/login" className="font-semibold text-primary hover:underline">
            Sign in
          </Link>
        </p>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-ink-muted">
        {label}
      </span>
      {children}
    </label>
  );
}
